import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import HiFiReviewForm from './HiFiReviewForm';
import HiFiReviewList from './HiFiReviewList';
import hifiReviewImage from '../assets/hifi-review.jpg'; // Import the image properly
import './HiFiReview.css';

const HiFiReviewBoard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [userId, setUserId] = useState(null);
  const [userDataLoaded, setUserDataLoaded] = useState(false); // Neuer State für Tracking
  
  useEffect(() => {
    // Authentifizierung prüfen
    const token = localStorage.getItem('token');
    setIsAuthenticated(!!token);
    
    if (token) {
      // WICHTIG: Hier ist die Änderung - sequentielle Ausführung sicherstellen
      const loadData = async () => {
        setLoading(true);
        try {
          // Zuerst Benutzerprofil laden
          const id = await fetchUserProfile();
          console.log("User ID geladen:", id);
          
          // Dann Reviews laden
          await fetchReviews();
          
          // Benutzer-Daten sind geladen
          setUserDataLoaded(true);
        } catch (error) {
          console.error("Fehler beim Laden der Daten:", error);
          setError("Fehler beim Laden der Daten. Bitte versuchen Sie es später erneut.");
        } finally {
          setLoading(false);
        }
      };
      
      loadData();
    } else {
      setLoading(false);
    }
  }, []);
  
  // Ändern Sie die fetchUserProfile-Funktion:
  const fetchUserProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Versuche zuerst den User aus localStorage zu bekommen
      const userObj = JSON.parse(localStorage.getItem('user') || '{}');
      if (userObj && userObj.id) {
        const userId = parseInt(userObj.id);
        console.log("User ID aus localStorage:", userId);
        setUserId(userId);
        return userId;
      }
      
      // Falls kein User im localStorage, versuche vom Server zu holen
      const response = await fetch('/api/user/profile', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch user profile');
      }
      
      const userData = await response.json();
      
      // Da keine ID im Profile ist, setzen wir die ID aus dem JWT-Token
      // (Der Server authentifiziert mit der ID aus dem Token)
      const getTokenData = () => {
        try {
          const decoded = JSON.parse(atob(token.split('.')[1]));
          return decoded;
        } catch (e) {
          return null;
        }
      };
      
      const tokenData = getTokenData();
      let userId = null;
      
      if (tokenData && tokenData.id) {
        userId = parseInt(tokenData.id);
        console.log("User ID aus Token extrahiert:", userId);
        
        // Speichere die User-ID im localStorage für zukünftige Nutzung
        if (userId) localStorage.setItem('userId', userId.toString());
      } else {
        // Alternative: Versuche die Admin API, falls vorhanden
        try {
          const adminResponse = await fetch('/api/admin/user/me', {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (adminResponse.ok) {
            const adminData = await adminResponse.json();
            if (adminData && adminData.id) {
              userId = parseInt(adminData.id);
              console.log("User ID aus Admin API:", userId);
            }
          }
        } catch (e) {
          console.log("Admin API nicht verfügbar");
        }
      }
      
      setUserId(userId);
      return userId;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }
  };
  
  const fetchReviews = async () => {
    try {
      const response = await fetch('/api/reviews', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch reviews');
      }
      
      const data = await response.json();
      setReviews(data.reviews || []);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      setError('Fehler beim Laden der Bewertungen. Bitte versuchen Sie es später erneut.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleReviewSubmitted = (newReview, action) => {
    if (action === 'create') {
      setReviews([newReview, ...reviews]);
    } else if (action === 'update') {
      setReviews(reviews.map(review => 
        review.id === newReview.id ? newReview : review
      ));
      setEditingReview(null);
    }
    setShowForm(false);
  };
  
  const handleEditReview = (review) => {
    setEditingReview(review);
    setShowForm(true);
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // Neue Funktion zum Löschen einer Bewertung
  const handleDeleteReview = async (reviewId) => {
    if (!confirm("Sind Sie sicher, dass Sie diese Bewertung löschen möchten?")) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/reviews/${reviewId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Fehler beim Löschen der Bewertung');
      }
      
      // Bewertung aus dem State entfernen
      setReviews(reviews.filter(review => review.id !== reviewId));
    } catch (error) {
      console.error('Error deleting review:', error);
      alert('Fehler beim Löschen der Bewertung: ' + error.message);
    }
  };
  
  // Update the unauthorized user message to be more informative
  if (!isAuthenticated) {
    return (
      <div className="hifi-review-container">
        <header className="review-header">
          <div className="review-header-image">
            <img src={hifiReviewImage} alt="HiFi Review Board" />
          </div>
          <p>Bewerte deine Audio-Komponenten und teile deine Erfahrungen mit der Community</p>
        </header>
        
        <div className="login-required-message">
          <span className="material-symbols-outlined">lock</span>
          <h3>Zugriff nur für angemeldete Benutzer</h3>
          <p>Um die Audio-Komponenten Bewertungen zu sehen und eigene Bewertungen zu erstellen, melden Sie sich bitte an oder registrieren Sie sich.</p>
          <div className="auth-buttons">
            <Link to="/login" className="btn btn-primary">Anmelden</Link>
            <Link to="/signup" className="btn btn-secondary">Registrieren</Link>
          </div>
        </div>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="hifi-review-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Lade Bewertungen...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="hifi-review-container">
        <div className="error-message">
          <span className="material-symbols-outlined">error</span>
          <p>{error}</p>
          <button 
            onClick={fetchReviews}
            className="retry-button"
          >
            <span className="material-symbols-outlined">refresh</span>
            Erneut versuchen
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="hifi-review-container">
      <header className="review-header">
        <div className="review-header-image">
          <img src={hifiReviewImage} alt="HiFi Review Board" />
        </div>
        <p>Bewerte deine Audio-Komponenten und teile deine Erfahrungen mit der Community</p>
      </header>
      
      <div className="review-actions">
        {!showForm ? (
          <button 
            className="add-review-button"
            onClick={() => {
              setEditingReview(null);
              setShowForm(true);
            }}
          >
            <span className="material-symbols-outlined">add_circle</span>
            Neue Bewertung erstellen
          </button>
        ) : (
          <button 
            className="cancel-button"
            onClick={() => {
              setEditingReview(null);
              setShowForm(false);
            }}
          >
            <span className="material-symbols-outlined">cancel</span>
            Abbrechen
          </button>
        )}
      </div>
      
      {showForm && (
        <HiFiReviewForm 
          onReviewSubmitted={handleReviewSubmitted} 
          onCancel={() => {
            setEditingReview(null);
            setShowForm(false);
          }}
          editReview={editingReview}
        />
      )}
      
      <HiFiReviewList 
        reviews={reviews} 
        userId={userId} 
        onEditReview={handleEditReview}
        onDeleteReview={handleDeleteReview}
      />
      {console.log("Rendering HiFiReviewList with userId:", userId, "userDataLoaded:", userDataLoaded)}
      {/* Log für Debugging */}
      {console.log("userId an HiFiReviewList übergeben:", userId, typeof userId)}
    </div>
  );
};

export default HiFiReviewBoard;